/**
 * The note shortcuts on the profile's Labels screen: the pills beside every
 * notes box, renamed, recoloured, reordered, added and deleted. A refused
 * change is said under the list; a refused rename also puts the saved label
 * back in its box.
 *
 * Used by: LabelsScreen (Profile > Labels), under the stages, and the
 * "+ Add a quick note" panel beside every notes box (QuickNoteChips). The notes
 * boxes on the plant cards read the same query key, so a change shows there
 * at once.
 *
 * @module NoteShortcutSettings
 */

import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, X, GripVertical } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { errorMessage } from '@/shared/lib/errorMessage';
import { FailureText } from '@/shared/components/ServerFailure';
import { Button } from '@/shared/components/ui/button';
import { Pill } from '@/shared/components/ui/Pill';
import { Input } from '@/shared/components/ui/input';
import { themeForColorKey, isColorKey, type ColorKey } from '@/shared/theme/colorPalette';
import {
  getNoteShortcuts, createNoteShortcut, updateNoteShortcut, deleteNoteShortcut,
  reorderNoteShortcuts, type NoteShortcut,
} from '@/shared/api/noteShortcuts';
import { useGrowLimits, useGrowSettings } from '@/shared/lib/growSettings';
import { useDragReorder } from '@/shared/hooks/useDragReorder';
import { ColorPicker } from './ColorPicker';
import { ActionRow } from '@/shared/components/ui/ActionRow';
import { BottomPanel } from '@/shared/components/ui/bottom-panel';
import { SavedTick } from './profileKit';
import { useSavedTicks } from './useSavedTicks';
import { NOTE_SHORTCUTS_QUERY_KEY } from '@/config/tunables/garden';

/**
 * The tappable pills, and the gardener's control over them.
 *
 * Every garden keeps needing to write down different things. Mine wants
 * "netted" and "slugs"; somebody growing under glass wants "vented". The four
 * this app shipped with are a starting point, not a vocabulary.
 *
 * @param props.inPanel - Shown in the "+ Add a quick note" panel (QuickNoteChips),
 *   whose own title already says "Quick notes": the heading is left out, the
 *   line under it stays.
 */
export const NoteShortcutSettings: React.FC<{ inPanel?: boolean }> = ({ inPanel = false }) => {
  const { reactTranslate: t } = useTranslation();
  const limits = useGrowLimits();
  const settings = useGrowSettings();
  const queryClient = useQueryClient();

  const [newLabel, setNewLabel] = React.useState('');
  // The colour the gardener picked for the new item; until they pick, the Grow setting's (the server's), else a neutral gray before it has been heard.
  const [pickedColor, setNewColor] = React.useState<ColorKey | null>(null);
  const settingColor = isColorKey(settings['notes.NEW_SHORTCUT_COLOR']) ? (settings['notes.NEW_SHORTCUT_COLOR'] as ColorKey) : null;
  const newColor: ColorKey = pickedColor ?? settingColor ?? 'gray';
  const [busy, setBusy] = React.useState(false);
  // True only while an Add is under way, so the Add button spins for its own add and not for a rename or a delete.
  const [adding, setAdding] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const { saved, flash } = useSavedTicks();
  const [deleting, setDeleting] = React.useState<{ id: number; label: string } | null>(null);

  const { data: shortcuts = [], refetch } = useQuery({
    queryKey: [NOTE_SHORTCUTS_QUERY_KEY],
    queryFn: getNoteShortcuts,
  });

  /** Runs one change and keeps its failure on screen rather than swallowing it. */
  const run = async (work: () => Promise<void>): Promise<boolean> => {
    setBusy(true);
    setError(null);
    try {
      await work();
      await refetch();
      // The notes screens draw their pills from the same cache key.
      await queryClient.invalidateQueries({ queryKey: [NOTE_SHORTCUTS_QUERY_KEY] });
      flash('shortcuts');
      return true;
    } catch (err) {
      setError(messageFor(err, t));
      return false;
    } finally {
      setBusy(false);
    }
  };

  /** Adds a shortcut by the typed label and chosen colour. */
  const handleAdd = async (): Promise<void> => {
    const label = newLabel.trim();
    if (!label) return;
    setAdding(true);
    try {
      await run(async () => {
        await createNoteShortcut(label, pickedColor ?? settingColor ?? undefined);
        setNewLabel('');
      });
    } finally {
      setAdding(false);
    }
  };

  // The pills appear beside every notes box in this order, so the one used
  // most can be dragged to the front.
  /**
   * A shortcut's id, for the drag list to tell its rows apart.
   *
   * @param shortcut - One of the gardener's note shortcuts.
   * @returns Its id.
   */
  const shortcutId = React.useCallback((shortcut: NoteShortcut) => shortcut.shortcut_id, []);
  const reorder = useDragReorder({
    items: shortcuts,
    idOf: shortcutId,
    disabled: busy,
    onCommit: async (ids) => {
      // A refused save has been reported on screen; the hook only needs to
      // know to put the rows back.
      if (!(await run(() => reorderNoteShortcuts(ids)))) throw new Error('not saved');
    },
  });

  return (
    <div className="space-y-3">
      <div>
        <p className={cn('flex items-center gap-2 text-base font-medium', inPanel && 'sr-only')}>
          {t('profile.labels.shortcuts.title')}
          <SavedTick show={saved('shortcuts')} />
        </p>
        <p className="text-sm text-muted-foreground">
          {t('profile.labels.shortcuts.description')}
        </p>
      </div>

      <ul className="space-y-2">
        {reorder.ordered.map((shortcut) => {
          const theme = themeForColorKey(shortcut.color_key);
          const lifted = reorder.dragging === shortcut.shortcut_id;
          return (
            <li
              key={shortcut.shortcut_id}
              {...reorder.rowProps(shortcut.shortcut_id)}
              className={cn(
                'flex items-center gap-2 rounded-sm transition-shadow',
                lifted && 'bg-muted shadow-md ring-1 ring-border',
              )}
            >
              <button
                type="button"
                {...reorder.handleProps(shortcut.shortcut_id)}
                disabled={busy}
                aria-label={t('profile.labels.reorder', { name: shortcut.label })}
                className="-my-1 flex h-9 w-9 cursor-grab items-center justify-center rounded-sm text-muted-foreground hover:text-foreground active:cursor-grabbing disabled:opacity-40"
              >
                <GripVertical className="h-4 w-4" aria-hidden="true" />
              </button>
              <ColorPicker
                value={shortcut.color_key}
                describedThing={shortcut.label}
                onChange={(colorKey) => run(() =>
                  updateNoteShortcut(shortcut.shortcut_id, { colorKey })
                )}
              />
              <Input
                defaultValue={shortcut.label}
                maxLength={limits.SHORTCUT_LABEL_MAX}
                disabled={busy}
                aria-label={t('profile.labels.shortcuts.labelFor', { label: shortcut.label })}
                // On Enter or on leaving the box rather than on every
                // keystroke: a shortcut is two words, and a request per
                // letter would be absurd.
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } }}
                onBlur={(e) => {
                  const label = e.target.value.trim();
                  if (!label || label === shortcut.label) {
                    e.target.value = shortcut.label;
                    return;
                  }
                  // Refused, the box goes back to the label that is saved; the
                  // reason is said under the list. It used to keep the refused
                  // words, which read as though they had been kept.
                  const box = e.currentTarget;
                  void run(() => updateNoteShortcut(shortcut.shortcut_id, { label })).then((ok) => { if (!ok) box.value = shortcut.label; });
                }}
                className="h-8 max-w-[16rem]"
              />
              <Pill size="sm" theme={theme} className="hidden shrink-0 sm:inline-flex">{shortcut.label}</Pill>
              <button
                type="button"
                onClick={() => setDeleting({ id: shortcut.shortcut_id, label: shortcut.label })}
                disabled={busy}
                aria-label={t('profile.labels.shortcuts.remove', { label: shortcut.label })}
                className="ml-auto rounded-sm p-1 text-muted-foreground hover:text-destructive disabled:opacity-40"
              >
                <Trash2 className="h-4 w-4" aria-hidden="true" />
              </button>
            </li>
          );
        })}
      </ul>

      <div className="flex items-center gap-2">
        <ColorPicker
          value={newColor}
          describedThing={t('profile.labels.shortcuts.newThing')}
          onChange={setNewColor}
        />
        <Input
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value.slice(0, limits.SHORTCUT_LABEL_MAX))}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void handleAdd(); } }}
          maxLength={limits.SHORTCUT_LABEL_MAX}
          disabled={busy}
          placeholder={t('profile.labels.shortcuts.placeholder')}
          aria-label={t('profile.labels.shortcuts.placeholder')}
          className="h-8 max-w-[16rem]"
        />
        <Button
          onClick={handleAdd}
          busy="adding"
          loading={adding}
          disabled={busy || newLabel.trim().length === 0}
          size="sm"
        >
          <Plus className="mr-1 h-4 w-4" aria-hidden="true" />
          {t('sitewide.add')}
        </Button>
      </div>

      {error && (
        <p className="flex items-center gap-1.5 text-sm text-destructive">
          <X className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span><FailureText text={error} /></span>
        </p>
      )}

      <BottomPanel
        open={deleting !== null}
        onOpenChange={(next) => { if (!next && !busy) setDeleting(null); }}
        title={t('profile.labels.deleteTitle', { name: deleting?.label ?? '' })}
      >
        <p className="text-sm text-muted-foreground">{t('profile.labels.shortcuts.deleteBody')}</p>
        {error && <p role="alert" className="text-sm text-destructive"><FailureText text={error} /></p>}
        <ActionRow cancelLabel={t('profile.labels.keep')} disabled={busy} onCancel={() => setDeleting(null)}>
          <Button
            variant="destructive"
            busy="deleting"
            loading={busy}
            onClick={async () => {
              if (!deleting) return;
              const id = deleting.id;
              // The question stays open until the server has answered: it closes on success, and on a failure it stays
              // with the failure words (run has already reported the failure).
              if (await run(() => deleteNoteShortcut(id))) setDeleting(null);
            }}
          >
            {t('profile.labels.delete')}
          </Button>
        </ActionRow>
      </BottomPanel>
    </div>
  );
};

/**
 * Turns the server's refusals into something a gardener can act on.
 *
 * @param err - Whatever the change threw.
 * @param t - The translator.
 * @returns A sentence for under the list, translated.
 */
function messageFor(err: unknown, t: (key: string, vars?: Record<string, string | number>) => string): string {
  const code = (err as { response?: { data?: { code?: string } } })?.response?.data?.code;
  if (code === 'DUPLICATE') return t('profile.labels.shortcuts.duplicate');
  if (code === 'TOO_MANY') return t('profile.labels.shortcuts.tooMany');
  // The server's own refusal where it gave one; never axios's "Request
  // failed with status code 500".
  return errorMessage(err, t('profile.labels.saveFailed'));
}
